import { useState, useEffect, useMemo } from 'react';
import { Dispatch, SetStateAction } from 'react';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { Modal, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useCreateCategory, useUpdateCategory } from '../api/hooks/useCategory';
import { useGetCategories } from '../api/hooks/useCategory';
import { showSuccess, showError } from '../helpers/toast';
import { CATEGORY_TYPE } from '../api/types/category.types';
import type { Category } from '../api/types/category.types';
import { NAME_LIMIT } from '../constants/settings';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  category?: Category | null; // Category to edit (null for create mode)
  type: typeof CATEGORY_TYPE.CATEGORY | typeof CATEGORY_TYPE.SUB_CATEGORY; // 1 for category, 2 for subcategory
  parentId?: string; // Required for subcategories
  onSuccess?: () => void; // Callback after successful create/update
}

export default function AddCategorySubcategory({
  open,
  onClose,
  setOpen,
  category,
  type,
  parentId,
  onSuccess,
}: ModalProps) {
  const [name, setName] = useState('');
  const [internalName, setInternalName] = useState('');
  const [status, setStatus] = useState(true);
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [error, setError] = useState(false);

  const isEditMode = !!category;
  const isSubcategory = type === CATEGORY_TYPE.SUB_CATEGORY;

  // Fetch categories for subcategory parent selection
  const { data: categoriesData } = useGetCategories(
    {},
    { enabled: isSubcategory && !isEditMode } // Only fetch when creating subcategory
  );

  // Ensure categories is always an array
  const categories = useMemo(() => {
    const data = categoriesData?.data;
    const list: Category[] = Array.isArray(data)
      ? data
      : Array.isArray((data as any)?.list)
        ? (data as any).list
        : Array.isArray((data as any)?.data)
          ? (data as any).data
          : [];
    return Array.isArray(list) ? list : [];
  }, [categoriesData]);

  // Reset form when modal opens/closes or category/type changes
  useEffect(() => {
    if (open) {
      if (category) {
        setName(category.name || '');
        setInternalName(category.internalName || '');
        setStatus(category.status !== undefined ? category.status : true);
        if (category.parentId) {
          setSelectedParentId(category.parentId);
        }
      } else {
        setName('');
        setInternalName('');
        setStatus(true);
        setSelectedParentId(parentId || '');
      }
      setError(false);
    }
  }, [open, category, parentId]);

  const { mutate: createCategory, isPending: isCreating } = useCreateCategory({
    onSuccess: (response) => {
      showSuccess(response.message || `${isSubcategory ? 'Subcategory' : 'Category'} created successfully`);
      handleClose();
      onSuccess?.();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || `Failed to create ${isSubcategory ? 'subcategory' : 'category'}`);
    },
  });

  const { mutate: updateCategory, isPending: isUpdating } = useUpdateCategory({
    onSuccess: (response) => {
      showSuccess(response.message || `${isSubcategory ? 'Subcategory' : 'Category'} updated successfully`);
      handleClose();
      onSuccess?.();
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || `Failed to update ${isSubcategory ? 'subcategory' : 'category'}`);
    },
  });

  const handleClose = () => {
    setName('');
    setInternalName('');
    setStatus(true);
    setSelectedParentId('');
    setError(false);
    setOpen(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      setError(true);
      showError('Name is required');
      return;
    }

    if (!internalName.trim()) {
      setError(true);
      showError('Internal name is required');
      return;
    }

    if (isSubcategory && !isEditMode && !selectedParentId) {
      setError(true);
      showError('Parent category is required for subcategories');
      return;
    }

    if (isEditMode && category?._id) {
      // Update existing category/subcategory
      updateCategory({
        id: category._id,
        data: {
          name: name.trim(),
          internalName: internalName.trim(),
          status,
        },
      });
    } else {
      // Create new category/subcategory
      const payload: any = {
        name: name.trim(),
        internalName: internalName.trim(),
        type,
        status,
      };

      if (isSubcategory) {
        payload.parentId = isEditMode ? category?.parentId : selectedParentId;
      }

      createCategory(payload);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Modal
      className="modal addNotification_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>
              {isEditMode
                ? `Edit ${isSubcategory ? 'Subcategory' : 'Category'}`
                : `Add ${isSubcategory ? 'Subcategory' : 'Category'}`}
            </h2>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isLoading}
            >
              <CloseIcon />
            </button>
          </div>
          <form className="form" onSubmit={handleSubmit}>
            {isSubcategory && !isEditMode && (
              <div className="control_group">
                <FormControl fullWidth>
                  <InputLabel id="parent-category-label">Parent Category</InputLabel>
                  <Select
                    labelId="parent-category-label"
                    id="parent-category"
                    value={selectedParentId}
                    onChange={(e) => setSelectedParentId(e.target.value)}
                    label="Parent Category"
                    disabled={isLoading}
                    required
                  >
                    {categories.map((cat: Category) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {error && !selectedParentId ? (
                  <h6 className="err_msg">Parent category is required</h6>
                ) : null}
              </div>
            )}

            <div className="control_group">
              <TextField
                placeholder={`${isSubcategory ? 'Subcategory' : 'Category'} Name`}
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                hiddenLabel
                disabled={isLoading}
                required
                inputProps={{ maxLength: NAME_LIMIT }}
                helperText={`${name.length}/${NAME_LIMIT} characters`}
              />
              {error && !name.trim() ? (
                <h6 className="err_msg">Name is required</h6>
              ) : null}
            </div>

            <div className="control_group">
              <TextField
                placeholder={`${isSubcategory ? 'Subcategory' : 'Category'} Internal Name`}
                fullWidth
                value={internalName}
                onChange={(e) => setInternalName(e.target.value)}
                hiddenLabel
                disabled={isLoading}
                required
                inputProps={{ maxLength: NAME_LIMIT }}
                helperText={`${internalName.length}/${NAME_LIMIT} characters`}
              />
              {error && !internalName.trim() ? (
                <h6 className="err_msg">Internal name is required</h6>
              ) : null}
            </div>

            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? 'Submitting...'
                  : isEditMode
                  ? 'Update'
                  : 'Submit'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}

